/**
 * Phase 14ai — read-only review panel for applicant-supplied data on the support
 * onboarding page. Hydrated from PractitionerOnboardingResponse.applicantDetails (returned
 * by the /api/secure/cc/support/practitioner-onboarding/detail endpoint).
 *
 * Three sub-panels:
 *   1. Banking — what the applicant typed during /apply Stage-1.5 enrichment, plus a
 *      Mark Verified button (with audit notes). The promotion onto formal PaymentConfig
 *      keys off this verified flag elsewhere in the onboarding flow.
 *   2. Documents — resumeFileId on ApplicantDetails plus the Practitioner-side credential /
 *      signature / profile picture media ids, all rendered as inline thumbnails (images) or
 *      open-in-new-tab links (PDFs / unknown types). Lets the reviewer see proof at a glance.
 *   3. Context — linkedinProfileUrl, currentAffiliations, recentCharges, longBio,
 *      websiteUrl, dateOfBirth, languages, location. Read-only; the agent can copy values
 *      into the editable form fields above when promoting onto Practitioner.
 *
 * Designed to render at the top of PractitionerOnboardingPage so the agent sees the
 * applicant's submission first, with the editable Practitioner form below it.
 */

import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DescriptionIcon from '@mui/icons-material/Description'
import { toast } from 'react-toastify'

import { markApplicantBankingVerified } from 'src/supportPortal/services/practitionerOnboardingService'

const MEDIA_BASE = 'https://media.aumhum.com/images/prod'

function mediaUrl(fileId) {
  const trimmed = (fileId || '').trim()
  if (!trimmed) return ''
  if (/^https?:/.test(trimmed)) return trimmed
  return `${MEDIA_BASE}/${trimmed}`
}

function isImageMediaId(fileId) {
  // Heuristic — the platform's MEDIA-* ids don't carry an extension, so we treat anything
  // that doesn't end in .pdf / .doc / .docx as a likely image. The img element handles
  // load errors silently (broken thumbnail) so the worst case is the reviewer falls back
  // to clicking the link.
  if (!fileId) return false
  const lc = fileId.toLowerCase()
  return !(lc.endsWith('.pdf') || lc.endsWith('.doc') || lc.endsWith('.docx'))
}

function formatDate(instantOrIsoString) {
  if (!instantOrIsoString) return ''
  const d = new Date(instantOrIsoString)
  if (Number.isNaN(d.getTime())) return String(instantOrIsoString)
  return d.toLocaleString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function FieldRow({ label, value, children }) {
  if (!value && !children) return null
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          minWidth: 140,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          fontSize: '11px',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {children || (
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

function MediaThumb({ fileId, label, fileName }) {
  const url = mediaUrl(fileId)
  if (!url) return null
  const showImage = isImageMediaId(fileId)
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        p: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        background: '#fafbfc',
      }}
    >
      {showImage ? (
        <Box
          component="img"
          src={url}
          alt={label}
          sx={{
            width: 72,
            height: 72,
            objectFit: 'cover',
            borderRadius: 1,
            background: '#fff',
            border: '1px solid #e5e7eb',
          }}
          onError={(e) => {
            // Fall back to a generic file icon if the image fails to load (e.g. PDF served as MEDIA-*).
            e.currentTarget.style.display = 'none'
          }}
        />
      ) : (
        <Box
          sx={{
            width: 72,
            height: 72,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 1,
            color: '#475569',
          }}
        >
          <DescriptionIcon fontSize="large" />
        </Box>
      )}
      <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {fileName && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {fileName}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            wordBreak: 'break-all',
            fontFamily: 'monospace',
            fontSize: '11px',
          }}
        >
          {fileId}
        </Typography>
        <MuiLink
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: '12px',
          }}
        >
          Open <OpenInNewIcon fontSize="inherit" />
        </MuiLink>
      </Stack>
    </Stack>
  )
}

function BankingSection({ practitionerId, banking, onVerified }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!banking) {
    return (
      <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
        Applicant did not enter banking details during /apply.
      </Alert>
    )
  }

  const verified = banking.verified === true
  const verifiedAt = banking.verifiedAt
  const verifiedBy = banking.verifiedBy
  const verifiedNotes = banking.verificationNotes

  async function handleConfirmVerify() {
    if (!practitionerId) {
      toast.error('Save the practitioner first.')
      return
    }
    setSubmitting(true)
    const result = await markApplicantBankingVerified({
      practitionerId,
      verified: true,
      notes: notes.trim(),
    })
    setSubmitting(false)
    if (!result.ok) {
      toast.error(result.message || 'Could not mark banking verified.')
      return
    }
    toast.success('Banking marked verified.')
    setDialogOpen(false)
    setNotes('')
    onVerified?.(result.data)
  }

  async function handleRevoke() {
    if (!practitionerId) return
    setSubmitting(true)
    const result = await markApplicantBankingVerified({
      practitionerId,
      verified: false,
      notes: '',
    })
    setSubmitting(false)
    if (!result.ok) {
      toast.error(result.message || 'Could not revoke banking verification.')
      return
    }
    toast.success('Banking verification revoked.')
    onVerified?.(result.data)
  }

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap' }}>
        {verified ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="Verified"
            color="success"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        ) : (
          <Chip
            icon={<HourglassEmptyIcon />}
            label="Unverified"
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: '#fef3c7',
              color: '#92400e',
            }}
          />
        )}
        {verified ? (
          <Button
            size="small"
            variant="text"
            color="warning"
            onClick={handleRevoke}
            disabled={submitting}
          >
            Revoke
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => setDialogOpen(true)}
            disabled={!practitionerId || submitting}
          >
            Mark verified
          </Button>
        )}
      </Stack>

      <Stack spacing={0.75}>
        <FieldRow label="Account holder" value={banking.accountHolderName} />
        <FieldRow label="Account number" value={banking.accountNumber} />
        <FieldRow label="IFSC" value={banking.ifsc} />
        <FieldRow label="Bank" value={banking.bankName} />
        <FieldRow label="Type" value={banking.accountType} />
        <FieldRow label="UPI" value={banking.upiId} />
        {banking.notes && <FieldRow label="Notes" value={banking.notes} />}
      </Stack>

      {verified && (verifiedBy || verifiedAt || verifiedNotes) && (
        <Box
          sx={{
            mt: 1,
            p: 1.25,
            borderRadius: 1,
            backgroundColor: '#dcfce7',
            border: '1px solid #bbf7d0',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#166534' }}>
            Verified by {verifiedBy || 'unknown'}
            {verifiedAt && ` · ${formatDate(verifiedAt)}`}
          </Typography>
          {verifiedNotes && (
            <Typography variant="body2" sx={{ mt: 0.5, color: '#166534' }}>
              {verifiedNotes}
            </Typography>
          )}
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark banking details verified</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This stamps the applicant&apos;s banking entries as verified by you and unlocks
            payout configuration. Add a note describing how you confirmed the details
            (e.g. penny-drop reference, branch call, passbook scan match).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Verification notes"
            placeholder="e.g. Penny-drop ₹1.00 cleared, IFSC lookup matched HDFC0000123, holder name match confirmed."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmVerify}
            disabled={submitting}
          >
            {submitting ? 'Marking…' : 'Mark verified'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

function DocumentsSection({ practitioner, applicantDetails }) {
  const items = useMemo(() => {
    const personalize = practitioner?.personalize || {}
    const arr = []
    if (practitioner?.profilePicId) {
      arr.push({
        key: 'profile',
        label: 'Profile photo',
        fileId: practitioner.profilePicId,
      })
    }
    if (personalize?.signatureMediaId) {
      arr.push({
        key: 'signature',
        label: 'Signature',
        fileId: personalize.signatureMediaId,
      })
    }
    if (personalize?.mandatoryCredentialDocFileId) {
      arr.push({
        key: 'credential',
        label: personalize.mandatoryCredentialDocTitle || 'Credential document',
        fileId: personalize.mandatoryCredentialDocFileId,
        fileName: personalize.mandatoryCredentialDocTitle,
      })
    }
    if (Array.isArray(personalize?.certificateIds) && personalize.certificateIds.length > 0) {
      personalize.certificateIds.forEach((fileId, idx) => {
        if (!fileId) return
        arr.push({
          key: `cert-${idx}`,
          label: `Certificate ${idx + 1}`,
          fileId,
        })
      })
    }
    if (applicantDetails?.resumeFileId) {
      arr.push({
        key: 'resume',
        label: 'Resume / CV',
        fileId: applicantDetails.resumeFileId,
        fileName: applicantDetails.resumeFileName,
      })
    }
    return arr
  }, [practitioner, applicantDetails])

  if (items.length === 0) {
    return (
      <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
        Applicant did not upload any documents during /apply.
      </Alert>
    )
  }

  return (
    <Grid container spacing={1.5}>
      {items.map((item) => (
        <Grid item xs={12} md={6} key={item.key}>
          <MediaThumb fileId={item.fileId} label={item.label} fileName={item.fileName} />
        </Grid>
      ))}
    </Grid>
  )
}

function ContextSection({ applicantDetails }) {
  if (!applicantDetails) {
    return (
      <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
        Practitioner was not created via /apply — no applicant context on file.
      </Alert>
    )
  }
  const {
    linkedinProfileUrl,
    websiteUrl,
    dateOfBirth,
    genderId,
    languages,
    longBio,
    location,
    currentAffiliations,
    hasOtherAffiliations,
    recentCharges,
    agreementsAccepted,
  } = applicantDetails
  return (
    <Stack spacing={1}>
      {linkedinProfileUrl && (
        <FieldRow label="LinkedIn">
          <MuiLink href={linkedinProfileUrl} target="_blank" rel="noopener noreferrer">
            {linkedinProfileUrl}
          </MuiLink>
        </FieldRow>
      )}
      {websiteUrl && (
        <FieldRow label="Website">
          <MuiLink href={websiteUrl} target="_blank" rel="noopener noreferrer">
            {websiteUrl}
          </MuiLink>
        </FieldRow>
      )}
      <FieldRow label="Date of birth" value={dateOfBirth} />
      <FieldRow label="Gender" value={genderId} />
      {Array.isArray(languages) && languages.length > 0 && (
        <FieldRow label="Languages">
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {languages.map((lang) => (
              <Chip key={lang} label={lang} size="small" variant="outlined" />
            ))}
          </Stack>
        </FieldRow>
      )}
      {longBio && (
        <FieldRow label="Long bio">
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {longBio}
          </Typography>
        </FieldRow>
      )}
      {location && (location.city || location.address || location.countryCode) && (
        <FieldRow label="Location">
          <Typography variant="body2">
            {[location.address, location.city, location.pincode, location.countryCode]
              .filter(Boolean)
              .join(', ')}
            {location.timeZoneId && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({location.timeZoneId})
              </Typography>
            )}
          </Typography>
        </FieldRow>
      )}
      {hasOtherAffiliations !== undefined && (
        <FieldRow
          label="Other affiliations?"
          value={hasOtherAffiliations ? 'Yes' : 'No'}
        />
      )}
      {Array.isArray(currentAffiliations) && currentAffiliations.length > 0 && (
        <FieldRow label="Current affiliations">
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {currentAffiliations.map((aff, idx) => (
              <Box
                key={`${aff.name || ''}-${idx}`}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  background: '#fafbfc',
                  border: '1px solid #e5e7eb',
                }}
              >
                {aff.name && (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {aff.name}
                  </Typography>
                )}
                {aff.url && (
                  <Typography variant="caption">
                    <MuiLink href={aff.url} target="_blank" rel="noopener noreferrer">
                      {aff.url}
                    </MuiLink>
                  </Typography>
                )}
                {aff.notes && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {aff.notes}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </FieldRow>
      )}
      {Array.isArray(recentCharges) && recentCharges.some((c) => c?.amount || c?.justification) && (
        <FieldRow label="Recent fees">
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {recentCharges
              .filter((c) => c?.amount || c?.justification)
              .map((c, idx) => (
                <Typography key={idx} variant="body2">
                  <strong>
                    {c.currencyCode || 'INR'} {c.amount ?? '—'}
                  </strong>
                  {c.justification ? ` — ${c.justification}` : ''}
                </Typography>
              ))}
          </Stack>
        </FieldRow>
      )}
      {Array.isArray(agreementsAccepted) && agreementsAccepted.length > 0 && (
        <FieldRow label="Agreements accepted">
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {agreementsAccepted.map((a, idx) => (
              <Typography key={`${a.agreementId}-${idx}`} variant="caption" color="text.secondary">
                <strong>{a.title || a.agreementId}</strong>
                {a.version && ` · v${a.version}`}
                {a.acceptedAt && ` · ${formatDate(a.acceptedAt)}`}
                {a.acceptedIp && ` · ip ${a.acceptedIp}`}
              </Typography>
            ))}
          </Stack>
        </FieldRow>
      )}
    </Stack>
  )
}

/**
 * Top-level panel rendered at the head of PractitionerOnboardingPage when
 * applicantDetails is present (or when any /apply-uploaded media exists on the
 * Practitioner). Hides itself entirely otherwise so practitioners created via the
 * direct support flow don't see an empty card.
 */
export default function ApplicantSubmissionsPanel({
  practitioner,
  applicantDetails,
  onBankingChanged,
}) {
  const practitionerId = practitioner?.id || ''
  const personalize = practitioner?.personalize || {}
  const hasAnyApplicantData = Boolean(
    applicantDetails ||
      practitioner?.profilePicId ||
      personalize?.signatureMediaId ||
      personalize?.mandatoryCredentialDocFileId ||
      (Array.isArray(personalize?.certificateIds) && personalize.certificateIds.length > 0),
  )
  if (!hasAnyApplicantData) return null

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 1.5, md: 2.5 },
        mb: 2,
        borderRadius: 2,
        borderColor: '#c7d2fe',
        background: 'linear-gradient(180deg, rgba(238, 242, 255, 0.45) 0%, rgba(255, 255, 255, 1) 100%)',
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Applicant submission
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Read-only snapshot of what the applicant entered during /apply. Use this to
            cross-check the editable form below; banking promotes onto PaymentConfig only
            after you mark it verified.
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Banking
          </Typography>
          <BankingSection
            practitionerId={practitionerId}
            banking={applicantDetails?.banking}
            onVerified={onBankingChanged}
          />
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Uploaded documents
          </Typography>
          <DocumentsSection
            practitioner={practitioner}
            applicantDetails={applicantDetails}
          />
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Applicant context
          </Typography>
          <ContextSection applicantDetails={applicantDetails} />
        </Box>
      </Stack>
    </Paper>
  )
}
